import { computed, type Ref } from 'vue'
import type { FormRules } from 'element-plus'

import type { ProjectMode } from '@/utils/projectModes'

export interface SubmitRequirementFormData {
  projectName: string
  projectMode: ProjectMode | ''
  productScenario: string
  duration: number
  targetAudience: string
  requirementText: string
  visualStyle: string
  referenceUrl: string
  remarks: string
  contentType: string
  contentBody: string
  productName: string
  productFeatures: string
  productScene: string
  scriptTitle: string
  scriptBody: string
}

interface ProductScenarioOption {
  label: string
  value: string
}

interface UseSubmitRequirementPageStateOptions {
  formData: SubmitRequirementFormData
  productScenarioOptions: ProductScenarioOption[]
  productDrivenRules: FormRules
  contentDrivenRules: FormRules
  scriptImportRules: FormRules
  contentCreationMode: Ref<'direct' | 'embed'>
  embeddedContent: Ref<string>
  draftEditorWaitingMessage: Ref<string>
  embeddedEditorWaitingMessage: Ref<string>
  contentConfirming: Ref<boolean>
  contentConfirmed: Ref<boolean>
  contentEmbedding: Ref<boolean>
  contentGeneratingAction: Ref<'generate' | 'refresh' | null>
  hasEmbeddedContent: Ref<boolean>
  taskPublishEnabled: Ref<boolean>
  isAdmin: Ref<boolean>
}

export function useSubmitRequirementPageState(options: UseSubmitRequirementPageStateOptions) {
  const formatDurationLabel = (duration: number) => {
    const seconds = Math.max(1, Math.round(Number(duration || 0) * 60))
    return `${seconds} 秒`
  }

  const modeSelected = computed(() => Boolean(options.formData.projectMode))
  const isProductDriven = computed(() => options.formData.projectMode === 'product_driven')
  const isContentDriven = computed(() => options.formData.projectMode === 'content_driven')
  const isScriptImport = computed(() => options.formData.projectMode === 'script_import')

  const activeRules = computed<FormRules>(() => {
    if (isProductDriven.value) return options.productDrivenRules
    if (isContentDriven.value) return options.contentDrivenRules
    if (isScriptImport.value) return options.scriptImportRules
    return {}
  })

  const requiresContentEmbedding = computed(() => isContentDriven.value && options.contentCreationMode.value === 'embed')
  const resolvedContentSubmission = computed(() => {
    if (!isContentDriven.value) {
      return ''
    }
    if (requiresContentEmbedding.value) {
      return options.embeddedContent.value.trim() || options.formData.contentBody.trim()
    }
    return options.formData.contentBody.trim()
  })

  const currentProductScenarioLabel = computed(() => {
    return options.productScenarioOptions.find((item) => item.value === options.formData.productScenario)?.label || '未选择'
  })

  const productPreviewPills = computed(() => [
    options.formData.productScenario ? `项目方向：${currentProductScenarioLabel.value}` : '',
    options.formData.targetAudience ? `用户：${options.formData.targetAudience}` : '',
    options.formData.visualStyle ? `风格：${options.formData.visualStyle}` : '',
    options.formData.duration ? `时长：${formatDurationLabel(options.formData.duration)}` : '',
  ].filter(Boolean))

  const firstStepTitle = computed(() => {
    if (isScriptImport.value) {
      return '先把项目名和剧本入口信息定下来'
    }
    if (isContentDriven.value) {
      return '先把内容方向和基础信息定下来'
    }
    return '先把项目基础信息和产品需求讲清楚'
  })

  const firstStepDescription = computed(() => {
    if (isScriptImport.value) {
      return '先填写项目名称，再补充可选的目标用户、风格和时长信息，后面导入的剧本会直接带着这些信息进入审批工作台。'
    }
    if (isContentDriven.value) {
      return '先填写项目名称、选择内容类型、目标时长和补充说明，后面生成的内容都会围绕这些信息展开。'
    }
    return '先填写项目名称、业务方向、目标用户、目标时长和需求说明，系统才知道后续该往什么方向生成。'
  })

  const isShowingEditorWaitingMessage = computed(() => Boolean(options.draftEditorWaitingMessage.value) && options.formData.contentBody === options.draftEditorWaitingMessage.value)
  const contentLength = computed(() => isShowingEditorWaitingMessage.value ? 0 : options.formData.contentBody.trim().length)
  const isShowingEmbeddedWaitingMessage = computed(() => Boolean(options.embeddedEditorWaitingMessage.value) && options.embeddedContent.value === options.embeddedEditorWaitingMessage.value)
  const canConfirmContent = computed(() => Boolean(options.formData.contentType && options.formData.contentBody.trim()) && !options.contentConfirming.value && !options.contentConfirmed.value)
  const canStartIntegration = computed(() => Boolean(options.formData.contentBody.trim() && options.formData.productName.trim() && options.formData.productFeatures.trim()) && !options.contentEmbedding.value)
  const isGeneratingContent = computed(() => options.contentGeneratingAction.value !== null)

  const integrationHint = computed(() => {
    if (!options.formData.contentBody.trim()) {
      return '先生成或写出一版内容，系统才能开始整合产品信息。'
    }
    if (!options.formData.productName.trim() || !options.formData.productFeatures.trim()) {
      return '把产品名称和卖点补充完整后，就可以启动内容整合。'
    }
    if (options.hasEmbeddedContent.value) {
      return '这版内容已经完成整合。如果你刚改过产品信息或正文，可以重新启动内容整合。'
    }
    return '内容和产品信息都准备好了，现在可以启动内容整合。'
  })

  const shouldShowTaskPublishPanel = computed(() => options.isAdmin.value && !isScriptImport.value)

  const submitButtonLabel = computed(() => {
    if (shouldShowTaskPublishPanel.value && options.taskPublishEnabled.value) {
      return '创建项目并发布'
    }
    if (isScriptImport.value) {
      return '创建项目并进入剧本审批'
    }
    return isContentDriven.value ? '创建项目并继续' : '创建项目并开始生成'
  })

  const submitHint = computed(() => {
    if (shouldShowTaskPublishPanel.value && options.taskPublishEnabled.value) {
      return '创建后会先生成项目记录，并同步发布到任务广场，不会直接进入自动生成流程。普通用户领取后，才会基于这份需求继续推进。'
    }
    if (isScriptImport.value) {
      return '创建后会直接把导入剧本挂到剧本审批节点，跳过需求分析和方案确认，由你直接开始修订和审批。'
    }
    if (isContentDriven.value) {
      return requiresContentEmbedding.value
        ? '创建后，系统会把内容类型、内容初稿、你修改后的版本，以及带入产品后的完整内容一起保存，然后继续进入后面的制作流程。'
        : '创建后，系统会直接使用当前内容进入项目流程；如果后面需要再做产品植入，仍可在工作台里继续调整。'
    }
    return '创建后，系统会根据你填写的产品需求先生成方案，再继续进入剧本和分镜制作。'
  })

  const submitDisabled = computed(() => {
    if (isContentDriven.value) {
      if (requiresContentEmbedding.value) {
        return !options.embeddedContent.value.trim() || !options.hasEmbeddedContent.value
      }
      return !options.formData.contentBody.trim()
    }
    if (isScriptImport.value) {
      return !options.formData.scriptBody.trim()
    }
    return false
  })

  const taskPublishSummarySource = computed(() => {
    if (isScriptImport.value) {
      return options.formData.scriptBody.trim()
    }
    if (isContentDriven.value) {
      return resolvedContentSubmission.value
    }
    return options.formData.requirementText.trim()
  })

  return {
    activeRules,
    canConfirmContent,
    canStartIntegration,
    contentLength,
    currentProductScenarioLabel,
    firstStepDescription,
    firstStepTitle,
    integrationHint,
    isContentDriven,
    isGeneratingContent,
    isProductDriven,
    isScriptImport,
    isShowingEditorWaitingMessage,
    isShowingEmbeddedWaitingMessage,
    modeSelected,
    productPreviewPills,
    requiresContentEmbedding,
    resolvedContentSubmission,
    shouldShowTaskPublishPanel,
    submitButtonLabel,
    submitDisabled,
    submitHint,
    taskPublishSummarySource,
  }
}